import { Search, SlidersHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export function PageTitle({
  eyebrow,
  title,
  subtitle,
  action
}: {
  eyebrow?: string;
  title: string;
  subtitle: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-title">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

export function FilterBar({
  search,
  onSearch,
  country,
  onCountry,
  category,
  onCategory
}: {
  search: string;
  onSearch: (value: string) => void;
  country: string;
  onCountry: (value: string) => void;
  category: string;
  onCategory: (value: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="filter-bar">
      <label className="search-field">
        <Search size={17} />
        <input
          value={search}
          onChange={(event) => onSearch(event.target.value)}
          placeholder={t("common.search")}
        />
      </label>
      <span className="filter-divider" />
      <SlidersHorizontal size={17} className="filter-icon" />
      <select value={country} onChange={(event) => onCountry(event.target.value)}>
        <option value="">{t("common.allMarkets")}</option>
        <option value="USA">United States</option>
        <option value="GBR">United Kingdom</option>
        <option value="IND">India</option>
        <option value="RUS">Russia</option>
        <option value="UKR">Ukraine</option>
      </select>
      <select value={category} onChange={(event) => onCategory(event.target.value)}>
        <option value="">{t("common.allProjects")}</option>
        <option value="blog">Blog</option>
        <option value="services">Services</option>
      </select>
    </div>
  );
}

export function RankBadge({ value }: { value: number | null | undefined }) {
  return (
    <span className={`rank-badge ${value == null ? "missing" : value <= 10 ? "top" : ""}`}>
      {value == null ? "—" : `#${value}`}
    </span>
  );
}

export function Change({ value }: { value: number | null | undefined }) {
  if (value == null || value === 0) return <span className="muted">—</span>;
  return (
    <span className={`table-change ${value > 0 ? "positive" : "negative"}`}>
      {value > 0 ? "↑" : "↓"} {Math.abs(value)}
    </span>
  );
}

export function Country({ code }: { code: string }) {
  const labels: Record<string, string> = {
    USA: "US",
    GBR: "UK",
    IND: "IN",
    RUS: "RU",
    UKR: "UA"
  };
  return <span className={`country-badge country-${code.toLowerCase()}`}>{labels[code] ?? code}</span>;
}

export function LoadingPanel() {
  const { t } = useTranslation();
  return (
    <div className="loading-panel">
      <span className="spinner" />
      {t("common.loading")}
    </div>
  );
}

export function ErrorPanel({ error }: { error: Error }) {
  const { t } = useTranslation();
  return (
    <div className="error-panel">
      <strong>{t("common.setup")}</strong>
      <span>{error.message}</span>
    </div>
  );
}
